import React, { useEffect, useState } from 'react'
import { Button, DatePicker, Form, Input, Select, Upload } from 'antd'
import { CheckCircleOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import { ApiRequest } from '../../../services/api/api'
import { baseUrl } from '../../../services/api/base-url'
import SweetAlertService from '../../../services/lib/utils/sweet-alert-service'
import moment from 'moment'
import type { UploadFile } from 'antd/es/upload/interface'
import { getAuthHeaders } from '../../../services/lib/utils/auth.utils'

interface MaintenanceLogEditDialogProps {
  logData: any
  onDialogClose: (success: boolean) => void
}

const STORED_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]'
const STORED_DATETIME_FORMATS_NO_OFFSET = ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm']

const toLocalMoment = (value: any) => {
  if (!value) return null
  const parsed = moment.utc(value, [STORED_DATETIME_FORMAT, ...STORED_DATETIME_FORMATS_NO_OFFSET], true)
  return parsed.isValid() ? parsed.local() : null
}

const MAINTENANCE_TYPE_OPTIONS = [
  { label: 'Routine', value: 'Routine' },
  { label: 'Breakdown Repair', value: 'Breakdown Repair' },
]

const YES_NO_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
]

const getImgs = (imgStr: string) => {
  if (!imgStr) return []
  try {
    const parsed = JSON.parse(imgStr)
    return Array.isArray(parsed) ? parsed : [parsed]
  } catch {
    return []
  }
}
const isAfterImg = (img: any) => img.stage === 'after'

// Owns both the edit-mode layout and the "update" API call for a maintenance log row.
const MaintenanceLogEditDialog = (props: MaintenanceLogEditDialogProps) => {
  const { logData, onDialogClose } = props
  const [form] = Form.useForm()
  const [machineOptions, setMachineOptions] = useState<{ label: string; value: string }[]>([])
  const [employeeOptions, setEmployeeOptions] = useState<{ label: string; value: number }[]>([])
  const toFileList = (imgs: any[]): UploadFile[] =>
    imgs.map((img: any, i: number) => ({
      uid: String(img.uid ?? i),
      name: img.url?.split('/').pop() || `image-${i}`,
      status: 'done' as const,
      url: img.url,
    }))
  const [beforeFileList, setBeforeFileList] = useState<UploadFile[]>(() =>
    toFileList(getImgs(logData.images).filter((img: any) => !isAfterImg(img)))
  )
  const [afterFileList, setAfterFileList] = useState<UploadFile[]>(() =>
    toFileList(getImgs(logData.images).filter(isAfterImg))
  )

  useEffect(() => {
    form.setFieldsValue({
      machineId: logData.machineId ?? undefined,
      maintenanceType: logData.maintenanceType ?? undefined,
      isIntrusiveMaintenance: logData.isIntrusiveMaintenance ?? undefined,
      problemDescription: logData.problemDescription,
      maintenanceDescription: logData.maintenanceDescription,
      timeStart: toLocalMoment(logData.timeStart) || moment(),
      timeFinish: toLocalMoment(logData.timeFinish) || moment(),
      replacementDetail: logData.replacementDetail,
      postMaintenanceClean: logData.postMaintenanceClean ?? undefined,
      maintenanceDoneById: logData.maintenanceDoneById ?? undefined,
      productionVerificationById: logData.productionVerificationById ?? undefined,
    })

    ApiRequest({ url: 'Machine/GetAllMachine', method: 'get' }).then((res: any) => {
      const machines = res?.data?.data || []
      setMachineOptions(machines.map((m: any) => ({ label: `${m.machineId}: ${m.machineName}`, value: m.machineId })))
    })

    ApiRequest({ url: 'Employee/GetAllEmployee', method: 'get' }).then((res: any) => {
      const employees = res?.data?.data || []
      setEmployeeOptions(employees.map((e: any) => ({ label: `${e.firstName}  ${e.lastName}`, value: e.employeeId })))
    })
  }, [])

  const onConfirm = async () => {
    try {
      const values = await form.validateFields()
      const payload = {
        ...logData,
        machineId: values.machineId,
        maintenanceType: values.maintenanceType,
        isIntrusiveMaintenance: values.isIntrusiveMaintenance,
        problemDescription: values.problemDescription,
        maintenanceDescription: values.maintenanceDescription,
        timeStart: values.timeStart.clone().utc().format(STORED_DATETIME_FORMAT),
        timeFinish: values.timeFinish.clone().utc().format(STORED_DATETIME_FORMAT),
        replacementDetail: values.replacementDetail,
        postMaintenanceClean: values.postMaintenanceClean,
        maintenanceDoneById: values.maintenanceDoneById,
        productionVerificationById: values.productionVerificationById,
        images: JSON.stringify([
          ...beforeFileList
            .filter((f) => f.status === 'done')
            .map((f, i) => ({ uid: i, url: f.url ?? f.response, stage: 'before' })),
          ...afterFileList
            .filter((f) => f.status === 'done')
            .map((f, i) => ({ uid: beforeFileList.length + i, url: f.url ?? f.response, stage: 'after' })),
        ]),
      }

      await ApiRequest({
        url: 'Maintenance/UpdateLog?id=' + logData.maintenanceLogId,
        method: 'put',
        data: payload,
        isShowSpinner: true,
      })

      await SweetAlertService.successMessage('Updated successfully')
      onDialogClose(true)
    } catch (error) {
      // validation error — antd will show inline messages
    }
  }

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <Form form={form} layout="vertical">
        <Form.Item label="Date">
          <Input value={logData.date ? moment(logData.date).format('YYYY-MM-DD HH:mm') : ''} disabled />
        </Form.Item>

        <Form.Item name="machineId" label="Machine Name" rules={[{ required: true, message: 'Required' }]}>
          <Select
            placeholder="Select machine"
            showSearch
            options={machineOptions}
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            allowClear
          />
        </Form.Item>

        <Form.Item name="maintenanceType" label="Maintenance Type">
          <Select placeholder="Select type" options={MAINTENANCE_TYPE_OPTIONS} allowClear />
        </Form.Item>

        <Form.Item name="isIntrusiveMaintenance" label="Is it Intrusive Maintenance">
          <Select placeholder="Select" options={YES_NO_OPTIONS} allowClear />
        </Form.Item>

        <Form.Item name="problemDescription" label="Problem Description" rules={[{ required: true, message: 'Required' }]}>
          <Input.TextArea rows={3} showCount maxLength={500} />
        </Form.Item>

        <Form.Item name="maintenanceDescription" label="Maintenance Description">
          <Input.TextArea rows={3} showCount maxLength={500} />
        </Form.Item>

        <Form.Item name="timeStart" label="Time Start">
          <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} allowClear={false} />
        </Form.Item>

        <Form.Item name="timeFinish" label="Time Finish">
          <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} allowClear={false} />
        </Form.Item>

        <Form.Item name="replacementDetail" label="Parts replacement detail, if any">
          <Input.TextArea rows={2} showCount maxLength={500} />
        </Form.Item>

        <Form.Item name="postMaintenanceClean" label="Post-Maintenance Clean Done?">
          <Select placeholder="Select" options={YES_NO_OPTIONS} allowClear />
        </Form.Item>

        <Form.Item name="maintenanceDoneById" label="Maintenance Done by">
          <Select
            placeholder="Search employee"
            showSearch
            options={employeeOptions}
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            allowClear
          />
        </Form.Item>

        <Form.Item name="productionVerificationById" label="Production Verification by">
          <Select
            placeholder="Search employee"
            showSearch
            options={employeeOptions}
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            allowClear
          />
        </Form.Item>

        <Form.Item label="Maintain Before">
        <Upload
          action={baseUrl + 'Common/UploadImage'}
          headers={getAuthHeaders()}
          name="imageFile"
            listType="picture"
            multiple
            fileList={beforeFileList}
            onChange={({ fileList: newList }) => setBeforeFileList(newList)}
            itemRender={(originNode, file, _list, actions) => (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {originNode}
                <DeleteOutlined onClick={actions.remove} style={{ color: 'red', marginLeft: 8, fontSize: 16, cursor: 'pointer' }} />
              </div>
            )}
          >
            <Button icon={<UploadOutlined />}>Upload</Button>
          </Upload>
        </Form.Item>

        <Form.Item label="Maintain After">
        <Upload
          action={baseUrl + 'Common/UploadImage'}
          headers={getAuthHeaders()}
          name="imageFile"
            listType="picture"
            multiple
            fileList={afterFileList}
            onChange={({ fileList: newList }) => setAfterFileList(newList)}
            itemRender={(originNode, file, _list, actions) => (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {originNode}
                <DeleteOutlined onClick={actions.remove} style={{ color: 'red', marginLeft: 8, fontSize: 16, cursor: 'pointer' }} />
              </div>
            )}
          >
            <Button icon={<UploadOutlined />}>Upload</Button>
          </Upload>
        </Form.Item>
      </Form>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
          paddingTop: '16px',
          borderTop: '1px solid #f0f0f0',
          marginTop: '8px',
        }}
      >
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={onConfirm}
          style={{ minWidth: '90px', backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          Update
        </Button>
      </div>
    </div>
  )
}

export default MaintenanceLogEditDialog
